import { MultiplayerRuntime } from "../MultiplayerRuntime";
import {
  createMultiplayerRuntimeHandle,
  MultiplayerRuntimeHandle,
} from "../handle/MultiplayerRuntimeHandle";
import { GameController } from "../../controller/GameController";
import { GameState } from "../../../core/protocol/types";
import {
  buildViewState,
  createInitialViewState,
  MultiplayerStatsSnapshot,
  MultiplayerViewState,
} from "./MultiplayerViewState";

type ViewStateListener = (state: MultiplayerViewState) => void;

export interface MultiplayerOrchestratorOptions {
  runtime?: MultiplayerRuntime;
  handle?: MultiplayerRuntimeHandle;
}

/**
 * MultiplayerOrchestrator owns a MultiplayerRuntime/handle pair and
 * exposes a derived MultiplayerViewState for React (or any host)
 * to consume. It mirrors the original C++ layering where UI simply
 * reacts to runtime state changes.
 */
export class MultiplayerOrchestrator {
  private readonly runtime: MultiplayerRuntime;
  private readonly handle: MultiplayerRuntimeHandle;
  private readonly listeners = new Set<ViewStateListener>();
  private unsubscribeRuntime?: () => void;

  private viewState: MultiplayerViewState = createInitialViewState();
  private currentGameState: GameState | null = null;
  private runtimeReady = false;
  private statsSnapshot?: MultiplayerStatsSnapshot;
  private errors: string[] = [];
  private disposed = false;

  constructor(options: MultiplayerOrchestratorOptions = {}) {
    this.runtime = options.runtime ?? new MultiplayerRuntime();
    this.handle =
      options.handle ?? createMultiplayerRuntimeHandle(this.runtime);

    this.subscribeToRuntime();
    this.refreshViewState();
  }

  /**
   * Subscribe to view state changes.
   */
  subscribe(listener: ViewStateListener): () => void {
    this.listeners.add(listener);
    listener(this.viewState);
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Get the latest cached view state.
   */
  getViewState(): MultiplayerViewState {
    return this.viewState;
  }

  /**
   * Mark runtime readiness (e.g., after host/join handshake completes).
   */
  setRuntimeReady(isReady: boolean): void {
    if (this.runtimeReady !== isReady) {
      this.runtimeReady = isReady;
      this.refreshViewState();
    }
  }

  /**
   * Update stats snapshot from runtime loop callbacks.
   */
  updateStats(stats: MultiplayerStatsSnapshot): void {
    this.statsSnapshot = stats;
    this.refreshViewState();
  }

  /**
   * Track non-fatal runtime errors so UI can surface them once.
   */
  reportError(message: string): void {
    this.errors = [...this.errors, message];
    this.refreshViewState();
  }

  /**
   * Clear collected error messages.
   */
  clearErrors(): void {
    if (this.errors.length > 0) {
      this.errors = [];
      this.refreshViewState();
    }
  }

  /**
   * Expose underlying runtime resources when platform code needs it.
   */
  getRuntime(): MultiplayerRuntime {
    return this.runtime;
  }

  getHandle(): MultiplayerRuntimeHandle {
    return this.handle;
  }

  getController(): GameController {
    return this.handle.controller;
  }

  /**
   * Dispose orchestrator resources.
   */
  dispose(): void {
    if (this.disposed) {
      return;
    }
    this.disposed = true;
    this.unsubscribeRuntime?.();
    this.handle.dispose();
    this.listeners.clear();
  }

  private subscribeToRuntime(): void {
    this.unsubscribeRuntime = this.runtime.subscribe((state) => {
      this.currentGameState = state;
      this.refreshViewState();
    });
  }

  private refreshViewState(): void {
    this.viewState = buildViewState({
      gameState: this.currentGameState,
      runtimeReady: this.runtimeReady,
      stats: this.statsSnapshot,
      errors: this.errors,
    });
    this.emit();
  }

  private emit(): void {
    this.listeners.forEach((listener) => listener(this.viewState));
  }
}

